<template>
  <div id="project" class="designspec--project">
    <div class="layout">
      <div class="project" v-if="project">
        <ProjectHeader :project="project" />
        <div
          :class="{ sections: true, 'collapse-all': isDragging }"
          ref="sections"
          v-stack-y:md
        >
          <CategorySelector
            :initial="!project.sections || (project.sections && project.sections.length === 0)"
          />
          <WelcomeSection
            v-if="!project.sections || (project.sections && project.sections.length === 0)"
          />
          <draggable
            v-model="sections"
            v-bind="dragOptions"
            v-stack-y:md
            @choose="handleChoose"
            @unchoose="handleUnchoose"
            @start="handleStart"
            @end="handleEnd"
          >
            <div
              class="wrapper"
              v-for="(section, i) in project.sections || []"
              :key="section.id"
            >
              <SpecSection
                :ref="'section-' + i"
                :key="section.id"
                :section="section"
                :project="project"
                @toggle="handleToggle($event)"
                @remove="handleRemove($event)"
              />
            </div>
          </draggable>
          <SloButton
            v-if="project.sections && project.sections.length > 0"
            fill="outline"
            @click="handleAdd()"
          >
            <SloIcon name="Plus" />
            <span>{{ 'Add a section' | t }}</span>
          </SloButton>
        </div>
      </div>
      <ProjectSidebar v-if="project" />
    </div>
    <SloModalView />
  </div>
</template>

<script>
import {mapGetters, mapActions, mapState, mapMutations} from 'vuex';
import FocusLock from 'vue-focus-lock';
import t from '../../main/i18n';
import store from '../../main/vue-store';
import draggable from 'vuedraggable';
import {getCoverImages, syncProject, getProject} from '../apis/designspec';
import {
  WelcomeSection,
  ProjectHeader,
  ProjectSidebar,
  SpecSection,
  CategorySelector
} from '../components';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  components: {
    draggable,
    WelcomeSection,
    ProjectHeader,
    ProjectSidebar,
    SpecSection,
    CategorySelector,
    FocusLock,
    SloButton,
    SloIcon,
    SloModalView: () => import('../../../lib/components/SloModalView.vue')
  },
  data() {
    return {
      dragOptions: {
        handle: '.spec-section-handle',
        animation: 400,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
        direction: 'vertical',
        swapThreshold: 0.5,
        sort: false,
        emptyInsertThreshold: 500,
        multiDrag: false,
        group: 'sections',
        setData: this.setData
      }
    };
  },
  filters: {
    t
  },
  watch: {
    project(project) {
      if (!project) {
        this.$router.replace('/designspec');
      }
    }
  },
  async beforeRouteEnter(to, from, next) {
    const projectId = Number(to.params.id);
    if (projectId === 0 && store.getters['auth/authenticated']) {
      next('/designspec/projects');
      return;
    }
    let project = store.getters['designspec/project'](projectId);
    if (!project) {
      project = store.getters['designspec/project'](`${projectId}`);
    }
    if (!project) {
      next();
      return;
    }

    let {sections} = project;
    if (!sections) sections = [];

    if (!store.getters['auth/authenticated']) {
      if (sections.length > 0) {
        syncProject(project)
          .then(newProject => {
            return store.dispatch('designspec/updateProject', {
              ...newProject,
              id: projectId
            });
          })
          .catch(e => {
            next(vm => vm.displayError(e, t('Unable to retrieve project details')));
          });
      }
    } else {
      await getProject(to.params.id)
        .then(project => store.dispatch('designspec/updateProject', project))
        .catch(e =>
          next(vm => vm.displayError(e, t('Unable to retrieve project details')))
        );
    }

    const coverImages = store.state['designspec/coverImages'];

    if (!coverImages) {
      getCoverImages()
        .then(images => {
          next(vm => vm.setCoverImages(null, images));
        })
        .catch(e => {
          next(vm => vm.setCoverImages(e, []));
        });
    } else {
      next(vm => vm.setCoverImages(null, coverImages));
    }
  },
  computed: {
    ...mapState('designspec', ['onboarded']),
    ...mapGetters('designspec', {getProject: 'project'}),
    project() {
      return this.getProject(Number(this.$route.params.id));
    },
    isDragging() {
      const meta = this.project.meta || {};
      return meta.isDraggingSection;
    },
    sections: {
      get() {
        return this.project.sections || [];
      },
      set(sections) {
        this.$store.dispatch('designspec/setSections', {
          project: this.project.id,
          sections
        });
      }
    }
  },
  methods: {
    ...mapMutations('designspec', ['setOnboarded']),
    ...mapActions('designspec', [
      'addSection',
      'removeSection',
      'toggleSection',
      'updateProjectMeta'
    ]),
    setData(dataTransfer, dragElement) {
      if ('setData' in dataTransfer) {
        dataTransfer.setData('text', `${dragElement.dataset.sectionId}`);
      }
    },
    handleChoose() {
      this.$nextTick(() => this.setIsDragging(true));
    },
    handleUnchoose() {
      this.setIsDragging(false);
    },
    handleStart() {
      this.setIsDragging(true);
    },
    handleEnd() {
      this.setIsDragging(false);
    },
    setIsDragging(isDraggingSection) {
      this.updateProjectMeta({
        id: this.project.id,
        isDraggingSection
      });
    },
    displayError(e, message) {
      this.$snotify.error(t(message));
    },
    setCoverImages(e, images) {
      if (e) {
        this.displayError(e, t('Unable to retrieve cover images'));
      } else {
        this.images = images;
        this.$store.commit('designspec/setCoverImages', images);
      }
    },
    handleAdd() {
      this.addSection({
        project: this.project.id
      });
    },
    handleToggle($event) {
      this.toggleSection({
        project: Number(this.$route.params.id),
        section: $event
      });
    },
    handleRemove($event) {
      this.removeSection({
        project: Number(this.$route.params.id),
        section: $event
      });
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.designspec--project {
  .layout {
    display: flex;
  }

  .project {
    width: 100%;
  }

  @media screen and (min-width: 1280px) {
    .project {
      width: 67%;
    }
  }

  .container {
    padding-left: 0;
    padding-right: 0;
  }
}

.text-sm {
  @include text('sm');
}

.sections {
  margin-bottom: 32px;

  > div > .wrapper + .wrapper {
    margin-top: 24px !important;
  }
}

.add-section {
  margin-bottom: 24px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms cubic-bezier(0.23, 1, 0.32, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
